<template>
	<div class="container">
		<Transition name="fade">
			<PageLoaderComponent v-if="loading" />
			<div v-else>
				<div class="mb-24">
					<h1 class="h1">
						Database
					</h1>
				</div>

				<TabNavigationComponent
					:tabs="tabs"
					:active-tab="active_tab"
					:disabled="loading_results"
					@set-tab="setTab"
				/>
				<DatabaseFiltersComponent />

				<DatabaseGridComponent
					:options="database_options"
					:loading="loading_results"
					@fetch-more-results="fetchMoreOptions"
					@update-database-likes="updateDatabaseLikes"
					@update-database-tags="updateDatabaseTags"
				/>

				<div class="flex flex--justify-center">
					<PageLoaderComponent
						v-if="database_options.length && database_options.length < pagination.total_count"
						ref="load_more_trigger"
						:small="true"
					/>
				</div>

			</div>
		</Transition>
	</div>
</template>

<script setup>
import DatabaseGridComponent from '../components/DatabaseGridComponent.vue';
import DatabaseFiltersComponent from '../components/DatabaseFiltersComponent.vue';
import PageLoaderComponent from '../components/PageLoaderComponent.vue';
import TabNavigationComponent from '../components/TabNavigationComponent';

import { USER_GROUP_CLIENT } from '../constants';

import moment from 'moment';
import { useDataStore } from '../stores/data';
import { useDatabaseFiltersStore } from '../stores/database-filters';
import { filterOutDuplicates } from '../../helpers';
import { isEqual } from 'underscore';
import { useElementVisibility } from '@vueuse/core';
import { useRoute, useRouter } from 'vue-router';
import { nextTick } from 'vue';
import { ref, computed, watch, onMounted } from 'vue';
import { storeToRefs } from 'pinia';

const data_store = useDataStore();
const filters_store = useDatabaseFiltersStore();
const router = useRouter();
const route = useRoute();

const {
	getDatabaseOptions,
	setBreadcrumbs
} = data_store;

const { user_group } = storeToRefs( data_store );

const {
	setSearchQuery,
	setActiveTab,
	setActiveProjectStatuses,
	getActiveTags,
	setActiveTags,
	getActiveProjectLocations,
	getActiveProjectTypes,
	getActiveOffices,
	getActiveMediaTypes,
	setActiveFilterValues,
	getActiveProjectManagers,
	getActiveAccountDirectors,
	getActiveLeadArtists,
	getActiveArtists,
	getActiveBusinessDevelopers,
	getActiveCompanies,
	setSelectedDateRange,
	setSelectedSorting,
	setShowFilters,
	updateQueryStringFromFilters,
	incrementPagination,
	resetPagination,
	updateTotalCount,
	selectMissingActiveDropdowns
} = filters_store;

const {
	active_dropdown_filters,
	active_tab,
	show_filters,
	client_services,
	architect,
	business_development,
	brand_partner,
	company,
	client,
	creative_agency,
	media_types,
	developer,
	development_consultant,
	hospitality_operator,
	interior_design,
	landscape_architect,
	lead_artist,
	artist,
	production_office,
	project_location,
	project_manager,
	pagination,
	search_query,
	sorting,
	date,
	project_status,
	tags,
	project_type,
	sales_marketing,
} = storeToRefs( filters_store );

const load_more_trigger = ref( null );
const trigger_is_visible = useElementVisibility( load_more_trigger );

const loading = ref( true );
const loading_results = ref( false );
const database_options = ref( [] );
const gallery_images = ref( null );
const show_gallery_modal = ref( false );

const tabs = ref( [
	{
		slug: 'all',
		label: 'All'
	},
	{
		slug: 'published',
		label: 'Published'
	}
] );

const setTab = ( value ) => {
	setActiveTab( value );
	resetPagination();
	updateQueryStringFromFilters();
}

const user_is_client = computed( () => {
	return user_group.value === USER_GROUP_CLIENT;
} );

const dateRangesByHandle = {
	'last_7_days': {
		start: moment().subtract( 7, 'days' ).format( 'YYYY-MM-DD' ),
		end: moment().format( 'YYYY-MM-DD' ),
	},
	'last_30_days': {
		start: moment().subtract( 30, 'days' ).format( 'YYYY-MM-DD' ),
		end: moment().format( 'YYYY-MM-DD' ),
	},
	'last_90_days': {
		start: moment().subtract( 90, 'days' ).format( 'YYYY-MM-DD' ),
		end: moment().format( 'YYYY-MM-DD' ),
	},
	'last_year': {
		start: moment().subtract( 1, 'years' ).format( 'YYYY-MM-DD' ),
		end: moment().format( 'YYYY-MM-DD' ),
	}
}

const sortingStringsByHandle = {
	'newest_first': 'postDate DESC',
	'oldest_first': 'postDate ASC',
	'most_popular': 'likes DESC',
	'least_popular': 'likes ASC',
}

const fetchOptions = async() => {
	loading_results.value = true;

	try {
		let params = {
			limit: pagination.value.limit,
			search: search_query.value,
			offset: ( pagination.value.page - 1 ) * pagination.value.limit
		};

		params.order_by = sortingStringsByHandle[sorting.value.selected ?? 'newest_first'];
		params.tags = getRelatedTagsArray();

		if ( date.value.selected ) {
			if ( ['last_7_days', 'last_30_days', 'last_90_days', 'last_year'].includes( date.value.selected ) ) {
				params.date = [
					'and',
					`>= ${dateRangesByHandle[date.value.selected].start}`,
					`<= ${dateRangesByHandle[date.value.selected].end}`
				];
			}
			if ( date.value.selected === 'custom' && date.value.custom?.length === 2 ) {
				params.date = [
					'and',
					`>= ${date.value.custom[0]}`,
					`<= ${date.value.custom[1]}`
				];
			}
		}
		if ( active_tab.value && active_tab.value === 'published' ) {
			params.published = true;
		}
		if ( project_location.value.active.length ) {
			params.location = project_location.value.active.map( location => parseInt( location.value ) );
		}
		if ( project_type.value.active.length ) {
			params.project_type = project_type.value.active.map( type => parseInt( type.value ) );
		}
		if ( media_types.value.active.length ) {
			params.media_type = media_types.value.active.map( type => parseInt( type.value ) );
		}
		if ( project_status.value.active.length ) {
			params.project_status = project_status.value.active.map( status => status.value );
		}
		if ( production_office.value.active.length ) {
			params.production_office = production_office.value.active.map( office => parseInt( office.value ) );
		}
		if ( company.value.active.length ) {
			params.company = company.value.active.map( company => parseInt( company.value ) );
		}
		if ( client.value.active.length ) {
			params.client = client.value.active.map( company => parseInt( company.value ) );
		}
		if ( brand_partner.value.active.length ) {
			params.brand_partner = brand_partner.value.active.map( company => parseInt( company.value ) );
		}
		if ( developer.value.active.length ) {
			params.developer = developer.value.active.map( company => parseInt( company.value ) );
		}
		if ( development_consultant.value.active.length ) {
			params.development_consultant = development_consultant.value.active.map( company => parseInt( company.value ) );
		}
		if ( creative_agency.value.active.length ) {
			params.creative_agency = creative_agency.value.active.map( company => parseInt( company.value ) );
		}
		if ( sales_marketing.value.active.length ) {
			params.sales_marketing = sales_marketing.value.active.map( company => parseInt( company.value ) );
		}
		if ( hospitality_operator.value.active.length ) {
			params.hospitality_operator = hospitality_operator.value.active.map( company => parseInt( company.value ) );
		}
		if ( architect.value.active.length ) {
			params.architect = architect.value.active.map( company => parseInt( company.value ) );
		}
		if ( landscape_architect.value.active.length ) {
			params.landscape_architect = landscape_architect.value.active.map( company => parseInt( company.value ) );
		}
		if ( interior_design.value.active.length ) {
			params.interior_design = interior_design.value.active.map( company => parseInt( company.value ) );
		}
		if ( project_manager.value.active.length ) {
			params.project_manager = project_manager.value.active.map( user => parseInt( user.value ) );
		}
		if ( client_services.value.active.length ) {
			params.client_services = client_services.value.active.map( user => parseInt( user.value ) );
		}
		if ( lead_artist.value.active.length ) {
			params.lead_artist = lead_artist.value.active.map( user => parseInt( user.value ) );
		}
		if ( artist.value.active.length ) {
			params.artist = artist.value.active.map( user => parseInt( user.value ) );
		}
		if ( business_development.value.active.length ) {
			params.business_development = business_development.value.active.map( user => parseInt( user.value ) );
		}
		const response = await getDatabaseOptions( params );

		if ( pagination.value.page === 1 ) {
			database_options.value = [];
		}

		addOptionsToArray( response.data.databaseOptions );
		updateTotalCount( response.data.total_count );
	} catch ( error ) {
		// console.log( error );
	}

	loading_results.value = false;
};

const fetchMoreOptions = async() => {
	incrementPagination();
	await nextTick();
	fetchOptions();
};

const addOptionsToArray = ( options_to_add ) => {
	database_options.value.push( ...options_to_add );
	database_options.value = filterOutDuplicates( database_options.value );
};

const updateResultsFromQueryString = async() => {
	const query = route.query;

	if ( query.tab ) {
		const tab = query.tab;
		setActiveTab( tab );
	} else {
		setActiveTab( 'all' );
	}

	setSearchQuery( query && query.search ? query.search : '' );

	let activated_dropdowns = [];

	if ( query.tag ) {
		const param_array = Array.isArray( query.tag ) ? query.tag : [query.tag];
		if ( param_array.map( tag_id => parseInt( tag_id ) ).sort() !== [...tags.value.active].map( tag => parseInt( tag.id ) ).sort() ) {
			await getActiveTags( query.tag );
		}
	} else {
		setActiveTags( [] );
	}
	if ( query.status ) {
		const param_array = Array.isArray( query.status ) ? query.status : [query.status];
		if ( param_array.sort() !== [...project_status.value.active].map( status => status.value ).sort() ) {
			setActiveProjectStatuses( param_array );
			activated_dropdowns.push( 'project_status' );
		}
	} else {
		setActiveProjectStatuses( [] );
	}
	if ( query.location ) {
		const param_array = Array.isArray( query.location ) ? query.location : [query.location];
		if ( param_array.map( location_id => parseInt( location_id ) ).sort() !== [...project_location.value.active].map( location => parseInt( location.value ) ).sort() ) {
			await getActiveProjectLocations( param_array );
			activated_dropdowns.push( 'project_location' );
		}
	} else {
		setActiveFilterValues( [], 'project_location' );
	}
	if ( query.type ) {
		const param_array = Array.isArray( query.type ) ? query.type : [query.type];
		if ( param_array.map( type_id => parseInt( type_id ) ).sort() !== [...project_type.value.active].map( type => parseInt( type.value ) ).sort() ) {
			await getActiveProjectTypes( param_array );
			activated_dropdowns.push( 'project_type' );
		}
	} else {
		setActiveFilterValues( [], 'project_type' );
	}
	if ( query.media_type ) {
		const param_array = Array.isArray( query.media_type ) ? query.media_type : [query.media_type];
		if ( param_array.map( media_type_id => parseInt( media_type_id ) ).sort() !== [...media_types.value.active].map( media_type => parseInt( media_type.value ) ).sort() ) {
			await getActiveMediaTypes( param_array );
			activated_dropdowns.push( 'media_types' );
		}
	} else {
		setActiveFilterValues( [], 'media_types' );
	}
	if ( query.production_office ) {
		const param_array = Array.isArray( query.production_office ) ? query.production_office : [query.production_office];
		if ( param_array.map( office_id => parseInt( office_id ) ).sort() !== [...production_office.value.active].map( office => parseInt( office.value ) ).sort() ) {
			await getActiveOffices( param_array );
			activated_dropdowns.push( 'production_office' );
		}
	} else {
		setActiveFilterValues( [], 'production_office' );
	}
	if ( query.company ) {
		const param_array = Array.isArray( query.company ) ? query.company : [query.company];
		if ( param_array.map( company_id => parseInt( company_id ) ).sort() !== [...company.value.active].map( company => parseInt( company.value ) ).sort() ) {
			await getActiveCompanies( param_array, 'company' );
			activated_dropdowns.push( 'company' );
		}
	} else {
		setActiveFilterValues( [], 'company' );
	}
	if ( query.client ) {
		const param_array = Array.isArray( query.client ) ? query.client : [query.client];
		if ( param_array.map( company_id => parseInt( company_id ) ).sort() !== [...client.value.active].map( company => parseInt( company.value ) ).sort() ) {
			await getActiveCompanies( param_array, 'client' );
			activated_dropdowns.push( 'client' );
		}
	} else {
		setActiveFilterValues( [], 'client' );
	}
	if ( query.brand_partner ) {
		const param_array = Array.isArray( query.brand_partner ) ? query.brand_partner : [query.brand_partner];
		if ( param_array.map( company_id => parseInt( company_id ) ).sort() !== [...brand_partner.value.active].map( company => parseInt( company.value ) ).sort() ) {
			await getActiveCompanies( param_array, 'brand_partner' );
			activated_dropdowns.push( 'brand_partner' );
		}
	} else {
		setActiveFilterValues( [], 'brand_partner' );
	}
	if ( query.developer ) {
		const param_array = Array.isArray( query.developer ) ? query.developer : [query.developer];
		if ( param_array.map( company_id => parseInt( company_id ) ).sort() !== [...developer.value.active].map( company => parseInt( company.value ) ).sort() ) {
			await getActiveCompanies( param_array, 'developer' );
			activated_dropdowns.push( 'developer' );
		}
	} else {
		setActiveFilterValues( [], 'developer' );
	}
	if ( query.development_consultant ) {
		const param_array = Array.isArray( query.development_consultant ) ? query.development_consultant : [query.development_consultant];
		if ( param_array.map( company_id => parseInt( company_id ) ).sort() !== [...development_consultant.value.active].map( company => parseInt( company.value ) ).sort() ) {
			await getActiveCompanies( param_array, 'development_consultant' );
			activated_dropdowns.push( 'development_consultant' );
		}
	} else {
		setActiveFilterValues( [], 'development_consultant' );
	}
	if ( query.creative_agency ) {
		const param_array = Array.isArray( query.creative_agency ) ? query.creative_agency : [query.creative_agency];
		if ( param_array.map( company_id => parseInt( company_id ) ).sort() !== [...creative_agency.value.active].map( company => parseInt( company.value ) ).sort() ) {
			await getActiveCompanies( param_array, 'creative_agency' );
			activated_dropdowns.push( 'creative_agency' );
		}
	} else {
		setActiveFilterValues( [], 'creative_agency' );
	}
	if ( query.sales_marketing ) {
		const param_array = Array.isArray( query.sales_marketing ) ? query.sales_marketing : [query.sales_marketing];
		if ( param_array.map( company_id => parseInt( company_id ) ).sort() !== [...sales_marketing.value.active].map( company => parseInt( company.value ) ).sort() ) {
			await getActiveCompanies( param_array, 'sales_marketing' );
			activated_dropdowns.push( 'sales_marketing' );
		}
	} else {
		setActiveFilterValues( [], 'sales_marketing' );
	}
	if ( query.hospitality_operator ) {
		const param_array = Array.isArray( query.hospitality_operator ) ? query.hospitality_operator : [query.hospitality_operator];
		if ( param_array.map( company_id => parseInt( company_id ) ).sort() !== [...hospitality_operator.value.active].map( company => parseInt( company.value ) ).sort() ) {
			await getActiveCompanies( param_array, 'hospitality_operator' );
			activated_dropdowns.push( 'hospitality_operator' );
		}
	} else {
		setActiveFilterValues( [], 'hospitality_operator' );
	}
	if ( query.architect ) {
		const param_array = Array.isArray( query.architect ) ? query.architect : [query.architect];
		if ( param_array.map( company_id => parseInt( company_id ) ).sort() !== [...architect.value.active].map( company => parseInt( company.value ) ).sort() ) {
			await getActiveCompanies( param_array, 'architect' );
			activated_dropdowns.push( 'architect' );
		}
	} else {
		setActiveFilterValues( [], 'architect' );
	}
	if ( query.landscape_architect ) {
		const param_array = Array.isArray( query.landscape_architect ) ? query.landscape_architect : [query.landscape_architect];
		if ( param_array.map( company_id => parseInt( company_id ) ).sort() !== [...landscape_architect.value.active].map( company => parseInt( company.value ) ).sort() ) {
			await getActiveCompanies( param_array, 'landscape_architect' );
			activated_dropdowns.push( 'landscape_architect' );
		}
	} else {
		setActiveFilterValues( [], 'landscape_architect' );
	}
	if ( query.interior_design ) {
		const param_array = Array.isArray( query.interior_design ) ? query.interior_design : [query.interior_design];
		if ( param_array.map( company_id => parseInt( company_id ) ).sort() !== [...interior_design.value.active].map( company => parseInt( company.value ) ).sort() ) {
			await getActiveCompanies( param_array, 'interior_design' );
			activated_dropdowns.push( 'interior_design' );
		}
	} else {
		setActiveFilterValues( [], 'interior_design' );
	}
	if ( query.project_manager ) {
		const param_array = Array.isArray( query.project_manager ) ? query.project_manager : [query.project_manager];
		if ( param_array.map( user_id => parseInt( user_id ) ).sort() !== [...project_manager.value.active].map( user => parseInt( user.value ) ).sort() ) {
			await getActiveProjectManagers( param_array );
			activated_dropdowns.push( 'project_manager' );
		}
	} else {
		setActiveFilterValues( [], 'project_manager' );
	}
	if ( query.client_services ) {
		const param_array = Array.isArray( query.client_services ) ? query.client_services : [query.client_services];
		if ( param_array.map( user_id => parseInt( user_id ) ).sort() !== [...client_services.value.active].map( user => parseInt( user.value ) ).sort() ) {
			await getActiveAccountDirectors( param_array );
			activated_dropdowns.push( 'client_services' );
		}
	} else {
		setActiveFilterValues( [], 'client_services' );
	}
	if ( query.lead_artist ) {
		const param_array = Array.isArray( query.lead_artist ) ? query.lead_artist : [query.lead_artist];
		if ( param_array.map( user_id => parseInt( user_id ) ).sort() !== [...lead_artist.value.active].map( user => parseInt( user.value ) ).sort() ) {
			await getActiveLeadArtists( param_array );
			activated_dropdowns.push( 'lead_artist' );
		}
	} else {
		setActiveFilterValues( [], 'lead_artist' );
	}
	if ( query.artist ) {
		const param_array = Array.isArray( query.artist ) ? query.artist : [query.artist];
		if ( param_array.map( user_id => parseInt( user_id ) ).sort() !== [...artist.value.active].map( user => parseInt( user.value ) ).sort() ) {
			await getActiveArtists( param_array );
			activated_dropdowns.push( 'artist' );
		}
	} else {
		setActiveFilterValues( [], 'artist' );
	}
	if ( query.business_development ) {
		const param_array = Array.isArray( query.business_development ) ? query.business_development : [query.business_development];
		if ( param_array.map( user_id => parseInt( user_id ) ).sort() !== [...business_development.value.active].map( user => parseInt( user.value ) ).sort() ) {
			await getActiveBusinessDevelopers( param_array );
			activated_dropdowns.push( 'business_development' );
		}
	} else {
		setActiveFilterValues( [], 'business_development' );
	}
	if ( query.sort ) {
		setSelectedSorting( query.sort );
	}
	if ( query.date_start && query.date_end ) {
		setSelectedDateRange( [query.date_start, query.date_end], true );
	}
	if ( query.period ) {
		setSelectedDateRange( query.period, false );
	}

	if ( activated_dropdowns.length ) {
		selectMissingActiveDropdowns( activated_dropdowns );
	}

	if ( !show_filters.value && ( tags.value.active.length || active_dropdown_filters.value.length ) ) {
		setShowFilters( true );
	}
};

const getRelatedTagsArray = () => {
	if ( !tags.value.active.length ) {
		return [];
	}
	return [ ...tags.value.active.map( tag => parseInt( tag.id ) ) ];
};

onMounted( async() => {
	loading.value = true;

	if ( user_is_client.value ) {
		router.push( { name: 'home' } );
		return;
	}

	setBreadcrumbs( [ { label: 'Database', icon: 'layout-grid' } ] );
	await updateResultsFromQueryString();
	await nextTick();
	await fetchOptions();

	loading.value = false;
} );

watch( trigger_is_visible, async( new_value, old_value ) => {
	if ( new_value && !old_value && !loading_results.value ) {
		await fetchMoreOptions();
	}
} );

// Watch for query change to refetch data
const route_query = computed( () => route.query );

watch( route_query, async( new_query, old_query ) => {
	if ( isEqual( new_query, old_query ) ) {
		return;
	}

	await updateResultsFromQueryString();
	await nextTick();
	await fetchOptions();
} );

function updateDatabaseLikes( select_option_id, likes_count ) {
	const option_index = database_options.value.findIndex( option => option.id === select_option_id );
	database_options.value[option_index].likes = likes_count;
}

function updateDatabaseTags( updated_database_options, select_option_id ) {
	const option_index = database_options.value.findIndex( option => option.id === select_option_id );
	database_options.value[option_index].database_tags = updated_database_options.database_tags;
}
</script>
